import React, { useState, useRef, useEffect } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  SearchOutlined, CloseOutlined, UpOutlined, DownOutlined, FolderOutlined,
} from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import T from './theme';
import { NATURES_COURRIER } from './categorieMessage';


import { localeIntl } from '../../i18n/format';
import { avecLibelleTraduit } from '../../i18n/libelles';


const FOLDER_OPTIONS = avecLibelleTraduit([
  { value: 'inbox' },
  { value: 'sent' },
  { value: 'suivis' },
  { value: 'drafts' },
  { value: 'relances' },
  { value: 'garanties' },
  { value: 'expertises' },
  { value: 'bap' },
  { value: 'bordereaux' },
], 'messagerie:dossiers');

/** Au-delà, la liste devient illisible - la troncature est annoncée, jamais silencieuse. */
const MAX_RESULTATS = 50;

export default function SearchOverlay({
  convs, sinistres, onClose, onSelectConv, onSelect,
  onRecherche, rechercheServeur = false, rechercheLoading = false, rechercheErreur = false,
}) {
  const { t } = useTranslation('messagerie');
  const handleSelect = onSelectConv || onSelect || (() => { });
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sinistre, setSinistre] = useState(null);
  const [nature, setNature] = useState(null);
  const [folder, setFolder] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [refined, setRefined] = useState(false);
  const [sinResults, setSinResults] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Recherche sinistres quand q change
  useEffect(() => {
    if (!q || q.length < 3) { setSinResults([]); return; }
    let cancelled = false;
    // skipErrorToast : un COURSIER n'a pas SINISTRE_READ et reçoit un 403 ici ; le .catch
    // juste en dessous laisse déjà les suggestions vides, un bandeau d'erreur n'apprendrait rien à l'agent.
    axiosInstance.get(`/v1/sinistres?search=${encodeURIComponent(q)}&size=5`, { skipErrorToast: true })
      .then(r => { if (!cancelled) setSinResults(r.data?.data || r.data?.content || []); })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [q]);

  /* Recherche serveur, avec anti-rebond : sans lui chaque frappe déclencherait une
     connexion IMAP. Le parent décide si le dossier courant est éligible. */
  useEffect(() => {
    if (!onRecherche) return undefined;
    const t = setTimeout(() => onRecherche(q), 350);
    return () => clearTimeout(t);
  }, [q, onRecherche]);

  // Filtre local conversations
  const ql = q.toLowerCase();
  const filtered = convs.filter(c => {
    /* Quand le serveur a fait la recherche, il est la référence sur le texte : le
       refiltrer localement écarterait des résultats légitimes (correspondance sur
       l'en-tête From brut, encodage du sujet…). Les critères ci-dessous ne servent
       plus alors qu'à affiner. */
    if (!rechercheServeur && ql
      && !`${c.sujet} ${c.preview} ${c.sinistre} ${c.participants.map(p => p.name + p.email).join(' ')}`.toLowerCase().includes(ql)) return false;
    if (from && !c.participants.some(p => (p.name + p.email).toLowerCase().includes(from.toLowerCase()))) return false;
    if (sinistre && c.sinistre !== sinistre) return false;
    if (nature && c.nature !== nature) return false;
    if (dateFrom && c.date && c.date < dateFrom) return false;
    if (dateTo && c.date && c.date > dateTo) return false;
    return true;
  });

  const hasFilters = from || to || sinistre || nature || folder || dateFrom || dateTo;
  const chipFilters = [
    from && { key: 'from', label: t('recherche.chipDe', { valeur: from }), clear: () => setFrom('') },
    sinistre && { key: 'sin', label: t('recherche.chipDossier', { valeur: sinistre }), clear: () => setSinistre(null) },
    nature && { key: 'nat', label: t('recherche.chipNature', { valeur: nature }), clear: () => setNature(null) },
  ].filter(Boolean);

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 60, fontFamily: T.font,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 680, background: '#fff',
        borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.25)',
        overflow: 'hidden', maxHeight: 'calc(100vh - 140px)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Barre de recherche */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderBottom: '1px solid #f0f0f0',
        }}>
          <span style={{ fontSize: 18, opacity: .5, display: 'flex' }}><SearchOutlined /></span>
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('recherche.placeholder')}
            style={{
              flex: 1, border: 'none', outline: 'none', fontSize: 16,
              fontFamily: 'inherit',
            }}
          />
          {q && (
            <button onClick={() => setQ('')} style={{ border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center' }}><CloseOutlined /></button>
          )}
          <kbd style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: '#9ca3af', background: '#f9fafb' }}>Esc</kbd>
        </div>

        {/* Chips de filtres actifs */}
        {chipFilters.length > 0 && (
          <div style={{ padding: '8px 16px', display: 'flex', flexWrap: 'wrap', gap: 6, borderBottom: '1px solid #f0f0f0' }}>
            {chipFilters.map(ch => (
              <span key={ch.key} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: '#e8f5ed', color: T.green, borderRadius: 20,
                padding: '3px 10px', fontSize: 12.5, fontWeight: 500,
              }}>
                {ch.label}
                <button onClick={ch.clear} style={{ border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center' }}><CloseOutlined /></button>
              </span>
            ))}
          </div>
        )}

        {/* Affiner */}
        <div style={{ padding: '0 16px', borderBottom: '1px solid #f0f0f0' }}>
          <button
            onClick={() => setRefined(r => !r)}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: T.green, fontSize: 12.5, fontWeight: 500, padding: '8px 0', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {refined ? <UpOutlined /> : <DownOutlined />} {t('recherche.affiner')}
          </button>
          {refined && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, paddingBottom: 12 }}>
              <FilterField label={t('recherche.de')} value={from} onChange={setFrom} placeholder={t('recherche.nomOuEmail')} />
              <FilterField label={t('recherche.a')} value={to} onChange={setTo} placeholder={t('recherche.nomOuEmail')} />
              <div>
                <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 4 }}>{t('recherche.numeroSinistre')}</div>
                <Select
                  size="small" allowClear showSearch style={{ width: '100%' }}
                  placeholder={t('recherche.sinistre')}
                  value={sinistre}
                  onChange={setSinistre}
                  options={(sinistres || []).map(s => ({ value: s.numero, label: s.numero }))}
                  filterOption={(i, opt) => (opt?.label || '').toLowerCase().includes(i.toLowerCase())}
                />
              </div>
              <div>
                <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 4 }}>{t('recherche.nature')}</div>
                <Select size="small" allowClear style={{ width: '100%' }} placeholder={t('recherche.nature')} value={nature} onChange={setNature} options={NATURES_COURRIER} />
              </div>
              <FilterField label={t('recherche.dateDepuis')} value={dateFrom} onChange={setDateFrom} type="date" />
              <FilterField label={t('recherche.dateJusqua')} value={dateTo} onChange={setDateTo} type="date" />
              <div>
                <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 4 }}>{t('recherche.dossier')}</div>
                <Select size="small" allowClear style={{ width: '100%' }} placeholder={t('recherche.dossier')} value={folder} onChange={setFolder} options={FOLDER_OPTIONS} />
              </div>
              {hasFilters && (
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    onClick={() => { setFrom(''); setTo(''); setSinistre(null); setNature(null); setDateFrom(''); setDateTo(''); setFolder(null); }}
                    style={{ border: '1px solid #e5e7eb', background: '#fff', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', color: '#6b7280' }}
                  >
                    {t('recherche.effacer')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Résultats */}
        <div className="cb-scroll" style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {/* Conversations trouvées */}
          {q && (
            <Section title={t('recherche.sectionConversations', { n: filtered.length })}>
              {rechercheLoading && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>
                  {t('recherche.enCours')}
                </div>
              )}
              {rechercheErreur && (
                /* Sans cet avertissement, l'utilisateur croirait avoir interrogé
                   toute sa boîte alors qu'il ne voit que les pages chargées. */
                <div style={{ padding: '12px 16px', fontSize: 12.5, color: '#b45309' }}>
                  {t('recherche.serveurIndisponible')}
                </div>
              )}
              {!rechercheLoading && filtered.length === 0 && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: '#9ca3af' }}>{t('recherche.aucune')}</div>
              )}
              {filtered.slice(0, MAX_RESULTATS).map(conv => (
                <ConvResult key={conv.id} conv={conv} q={ql} onSelect={() => { handleSelect(conv); onClose(); }} />
              ))}
              {filtered.length > MAX_RESULTATS && (
                <div style={{ padding: '10px 16px', fontSize: 12, color: '#9ca3af' }}>
                  {t('recherche.tronque', { max: MAX_RESULTATS, total: filtered.length })}
                </div>
              )}
            </Section>
          )}

          {/* Sinistres liés */}
          {sinResults.length > 0 && (
            <Section title={t('recherche.sectionSinistres', { n: sinResults.length })}>
              {sinResults.map((s, i) => (
                <SinistreResult key={i} s={s} onClick={() => { setSinistre(s.numero); setQ(''); setSinResults([]); }} />
              ))}
            </Section>
          )}

          {/* État vide initial */}
          {!q && !hasFilters && (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 32, opacity: .25, marginBottom: 12, display: 'flex', justifyContent: 'center' }}><SearchOutlined /></div>
              <div style={{ fontSize: 13.5, color: '#6b7280' }}>{t('recherche.invite')}</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>{t('recherche.inviteAide')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ padding: '10px 16px 4px', fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: '#9ca3af' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function FilterField({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <input
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', border: '1px solid #e5e7eb', borderRadius: 6,
          padding: '5px 8px', fontSize: 12.5, fontFamily: 'inherit',
          boxSizing: 'border-box', outline: 'none',
        }}
      />
    </div>
  );
}

function highlight(text, q) {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: '#bbf7d0', padding: 0 }}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function ConvResult({ conv, q, onSelect }) {
  const [hov, setHov] = useState(false);
  const p0 = conv.participants[0];
  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '10px 16px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start',
        background: hov ? '#f0fdf4' : 'transparent',
      }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: '50%', background: T.green,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 600, flexShrink: 0,
      }}>
        {(p0?.name?.[0] || p0?.email?.[0] || '?').toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
          {highlight(conv.sujet, q)}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p0?.name || p0?.email} · {highlight(conv.preview || '', q)}
        </div>
        {conv.sinistre && (
          <span style={{ fontSize: 11, background: '#dbeafe', color: '#1e40af', borderRadius: 3, padding: '1px 6px', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <FolderOutlined /> {conv.sinistre}
          </span>
        )}
      </div>
    </div>
  );
}

function SinistreResult({ s, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '10px 16px', cursor: 'pointer',
        background: hov ? '#f0fdf4' : 'transparent',
        display: 'flex', gap: 10, alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 18, display: 'flex' }}><FolderOutlined /></span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{s.numero}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{s.assureNom || s.nomAssure} · {s.dateAccident ? new Date(s.dateAccident).toLocaleDateString(localeIntl()) : ''}</div>
      </div>
    </div>
  );
}
